/**
 * Enereg — semantic derivation
 * =========================================================================
 * ONE source of truth for every "number or status → colour" decision.
 *
 * Before this file the codebase held five conflicting score→colour rules, so
 * the same 65% readiness rendered amber on one page and green on another;
 * three parallel bankability vocabularies; and seven independent type→colour
 * maps that each re-encoded the same idea. A licence-status enum was reused to
 * render document state and alert severity, so a finished extraction displayed
 * a green "Approved" pill.
 *
 * Those five score rules were never five bugs. They were three legitimate
 * SCALES plus two accidents. Scales are declared here; accidents are gone.
 *
 * Every function returns token-derived class names, never a hex. Every status
 * also returns a glyph, because colour alone must never carry meaning.
 *
 * The conformance gate (design/gate/conformance.ts) fails the build if a
 * status→colour map appears anywhere else.
 */

/**
 * Canonical extraction-safety constants. They live HERE (dependency-free,
 * browser-safe) and the server pipeline imports them from this module —
 * importing the other way drags bun:sqlite into the frontend bundle and
 * 500s the SPA. One definition either way; direction chosen by purity.
 */
export const ALWAYS_CONFIRM_FIELDS: readonly string[] = ["F-006", "P-002", "S-011"];
export const CONFIDENCE_THRESHOLD = 0.80;

/* ===========================================================================
   BANDS
   =========================================================================== */

export type Band = "strong" | "adequate" | "marginal" | "insufficient";

/** A measurement whose thresholds are a property of what is being measured. */
export type Scale = "readiness" | "bankability" | "confidence";

/**
 * Descending cut-offs. A value lands in the first band it meets or exceeds;
 * anything below the last cut-off is `insufficient`.
 *
 * These differ per scale on purpose. 70% readiness means "well underway";
 * 70% extraction confidence means "do not trust this without a human". The old
 * code disagreed with itself because it never wrote that distinction down.
 */
const CUTOFFS: Record<Scale, ReadonlyArray<readonly [number, Band]>> = {
  readiness: [[60, "strong"], [30, "adequate"], [0, "marginal"]],
  // The 4-band Checkup rule wins: it is the only one that distinguished
  // "we scored you low" from "we could not score you".
  bankability: [[75, "strong"], [50, "adequate"], [25, "marginal"]],
  // Anchored to the pipeline's own CONFIDENCE_THRESHOLD (imported above), so
  // what the UI presents as "auto-commit" is what the server enforces.
  confidence: [[CONFIDENCE_THRESHOLD * 100, "strong"], [60, "adequate"]],
};

/** The single scoring function. No component may inline a threshold. */
export function band(value: number | null | undefined, scale: Scale): Band {
  if (value === null || value === undefined || Number.isNaN(value)) return "insufficient";
  for (const [floor, name] of CUTOFFS[scale]) if (value >= floor) return name;
  return "insufficient";
}

export interface Style {
  label: string;
  bg: string;
  text: string;
  icon: string;
  /** Solid dot, for timelines and provenance markers. */
  dot?: string;
  /** Left accent rule, for callout cards. */
  accent?: string;
}

/** Bands carry an icon as well as a colour: meaning must survive greyscale. */
const BAND_STYLE: Record<Band, Style> = {
  strong:       { label: "Strong",            bg: "bg-brand-light",      text: "text-brand",         icon: "●" },
  adequate:     { label: "Adequate",          bg: "bg-info-light",       text: "text-info",          icon: "◐" },
  marginal:     { label: "Marginal",          bg: "bg-warning-light",    text: "text-warning-text",  icon: "◔" },
  insufficient: { label: "Insufficient data", bg: "bg-surface-tertiary", text: "text-text-secondary", icon: "○" },
};

export const bandStyle = (b: Band): Style => BAND_STYLE[b];

/** Solid fill for progress bars: the band's hue at full strength. */
const BAND_BAR: Record<Band, string> = {
  strong: "bg-brand", adequate: "bg-info", marginal: "bg-warning-fill", insufficient: "bg-danger",
};
export const bandBar = (b: Band): string => BAND_BAR[b];

/** Stroke/text colour beside a bar or inside an SVG ring (non-text UI). */
const BAND_TEXT: Record<Band, string> = {
  strong: "text-brand", adequate: "text-info", marginal: "text-warning-fill", insufficient: "text-danger",
};
export const bandText = (b: Band): string => BAND_TEXT[b];

/* ===========================================================================
   BANKABILITY — one truth, several views
   =========================================================================== */

/**
 * Letter grades and prose labels are PRESENTATIONS of a band, not parallel
 * sources of truth. Previously a project could be grade "B" on the dashboard
 * and "marginal" in the evaluation panel simultaneously.
 */
export const bankabilityLabel = (b: Band): string => BAND_STYLE[b].label;

export const bankabilityGrade = (b: Band): "A" | "B" | "C" | "—" =>
  ({ strong: "A", adequate: "B", marginal: "C", insufficient: "—" } as const)[b];

/* ===========================================================================
   STATUS
   =========================================================================== */

export type StatusKind =
  | "license"       // the regulatory application lifecycle
  | "licenseEvent"  // an entry in a licence's timeline
  | "compliance"    // an obligation's assessment state
  | "tier"          // a government requirement's tier
  | "extraction"    // a document's processing state
  | "alert"         // a regulatory-change notification's severity
  | "activity"      // an entry in the audit log
  | "task"          // a guided next action
  | "nudge"         // an AI recommendation
  | "criticality"   // how badly a gap hurts
  | "toast";        // a transient confirmation

/**
 * Keyed by (kind, value). Previously `StatusPill` accepted only the licence
 * enum, so callers coerced foreign concepts into it — document
 * `completed → approved`, alert `blocking → blocked`. Those coercions are now
 * explicit rows with their own vocabulary.
 *
 * "Blocked" ≠ "Not started": a licence that CANNOT be started yet reads
 * differently from one that merely HASN'T been.
 */
const STATUS: Record<StatusKind, Record<string, Style>> = {
  license: {
    not_started:               { label: "Not started",    bg: "bg-surface-tertiary", text: "text-text-secondary", icon: "○" },
    blocked:                   { label: "Blocked",        bg: "bg-danger-light",     text: "text-danger",         icon: "⊘" },
    preparing:                 { label: "Preparing",      bg: "bg-warning-light",    text: "text-warning-text",   icon: "◔" },
    additional_info_requested: { label: "Info requested", bg: "bg-warning-light",    text: "text-warning-text",   icon: "!" },
    submitted:                 { label: "Submitted",      bg: "bg-info-light",       text: "text-info",           icon: "↑" },
    under_review:              { label: "Under review",   bg: "bg-info-light",       text: "text-info",           icon: "◐" },
    approved:                  { label: "Approved",       bg: "bg-brand-light",      text: "text-brand",          icon: "✓" },
    rejected:                  { label: "Rejected",       bg: "bg-danger-light",     text: "text-danger",         icon: "×" },
    expired:                   { label: "Expired",        bg: "bg-danger-light",     text: "text-danger",         icon: "⌛" },
    unrecognized:              { label: "Unrecognised",   bg: "bg-surface-tertiary", text: "text-text-tertiary",  icon: "?" },
  },
  licenseEvent: {
    submitted:      { label: "Submitted",      bg: "bg-info-light",       text: "text-info",           icon: "↑", dot: "bg-info" },
    acknowledged:   { label: "Acknowledged",   bg: "bg-info-light",       text: "text-info",           icon: "◐", dot: "bg-info" },
    under_review:   { label: "Under review",   bg: "bg-warning-light",    text: "text-warning-text",   icon: "◔", dot: "bg-warning-fill" },
    info_requested: { label: "Info requested", bg: "bg-warning-light",    text: "text-warning-text",   icon: "!", dot: "bg-warning-fill" },
    info_provided:  { label: "Info provided",  bg: "bg-info-light",       text: "text-info",           icon: "↑", dot: "bg-info" },
    approved:       { label: "Approved",       bg: "bg-brand-light",      text: "text-brand",          icon: "✓", dot: "bg-brand" },
    rejected:       { label: "Rejected",       bg: "bg-danger-light",     text: "text-danger",         icon: "×", dot: "bg-danger" },
    appealed:       { label: "Appealed",       bg: "bg-warning-light",    text: "text-warning-text",   icon: "↺", dot: "bg-warning-fill" },
    renewed:        { label: "Renewed",        bg: "bg-brand-light",      text: "text-brand",          icon: "✓", dot: "bg-brand" },
    note:           { label: "Note",           bg: "bg-surface-secondary", text: "text-text-secondary", icon: "✎", dot: "bg-hairline" },
  },
  compliance: {
    not_assessed:   { label: "Not assessed",  bg: "bg-surface-tertiary", text: "text-text-secondary", icon: "○" },
    compliant:      { label: "Compliant",     bg: "bg-brand-light",      text: "text-brand",          icon: "✓" },
    in_progress:    { label: "In progress",   bg: "bg-info-light",       text: "text-info",           icon: "◐" },
    non_compliant:  { label: "Non-compliant", bg: "bg-danger-light",     text: "text-danger",         icon: "×" },
    exempt:         { label: "Exempt",        bg: "bg-warning-light",    text: "text-warning-text",   icon: "–" },
    not_applicable: { label: "N/A",           bg: "bg-surface-tertiary", text: "text-text-tertiary",  icon: "–" },
  },
  tier: {
    gate:            { label: "Gate",            bg: "bg-danger-light",  text: "text-danger",       icon: "⊘" },
    pre_operational: { label: "Pre-operational", bg: "bg-warning-light", text: "text-warning-text", icon: "◔" },
    ongoing:         { label: "Ongoing",         bg: "bg-info-light",    text: "text-info",         icon: "◐" },
  },
  // Keys mirror the source_document.extraction_status CHECK constraint in
  // src/db/schema.ts exactly — 'pending' is the column DEFAULT. A key that
  // the database never stores is a vocabulary bug, not a style choice.
  extraction: {
    pending:        { label: "Pending",    bg: "bg-surface-tertiary", text: "text-text-secondary", icon: "○" },
    processing:     { label: "Processing", bg: "bg-info-light",       text: "text-info",           icon: "◐" },
    // Not "Approved". A finished extraction is not an approved licence.
    completed:      { label: "Extracted",  bg: "bg-ai-light",         text: "text-ai",             icon: "✦" },
    failed:         { label: "Failed",     bg: "bg-danger-light",     text: "text-danger",         icon: "×" },
    not_applicable: { label: "N/A",        bg: "bg-surface-tertiary", text: "text-text-tertiary",  icon: "–" },
  },
  alert: {
    informational:      { label: "For information", bg: "bg-info-light",    text: "text-info",         icon: "i", accent: "border-info" },
    action_recommended: { label: "Recommended",     bg: "bg-warning-light", text: "text-warning-text", icon: "◔", accent: "border-warning-fill" },
    action_required:    { label: "Action required", bg: "bg-danger-light",  text: "text-danger",       icon: "!", accent: "border-danger" },
    blocking:           { label: "Blocking",        bg: "bg-danger-light",  text: "text-danger",       icon: "⊘", accent: "border-danger" },
  },
  activity: {
    field_update:    { label: "Field updated",  bg: "bg-surface-tertiary", text: "text-text-secondary", icon: "✎" },
    // The system did this for you — purple, per the provenance canon.
    field_update_machine: { label: "Field filled for you", bg: "bg-ai-light", text: "text-ai",          icon: "✦" },
    document_upload: { label: "Document added", bg: "bg-brand-light",      text: "text-brand",          icon: "↑" },
    license_event:   { label: "Licence event",  bg: "bg-info-light",       text: "text-info",           icon: "◆" },
    alert:           { label: "Alert",          bg: "bg-danger-light",     text: "text-danger",         icon: "!" },
  },
  task: {
    submit_application: { label: "Ready to submit", bg: "bg-brand-light",       text: "text-brand",          icon: "↑" },
    review_extraction:  { label: "Review needed",   bg: "bg-warning-light",     text: "text-warning-text",   icon: "⚠" },
    upload_document:    { label: "Upload",          bg: "bg-info-light",        text: "text-info",           icon: "↑" },
    fill_field:         { label: "Fill field",      bg: "bg-surface-secondary", text: "text-text-secondary", icon: "✎" },
    record_event:       { label: "Record",          bg: "bg-warning-light",     text: "text-warning-text",   icon: "◔" },
    // "Does X apply to you?" — a question to settle, not work to do.
    confirm_applicability: { label: "Confirm",      bg: "bg-info-light",        text: "text-info",           icon: "?" },
  },
  nudge: {
    critical_path:   { label: "Critical path",   bg: "bg-danger-light/30",  text: "text-danger",       icon: "⊘", accent: "border-l-danger" },
    regulatory_risk: { label: "Regulatory risk", bg: "bg-danger-light/30",  text: "text-danger",       icon: "!", accent: "border-l-danger" },
    gap:             { label: "Gap",             bg: "bg-warning-light/30", text: "text-warning-text", icon: "◔", accent: "border-l-warning" },
    gap_quick_win:   { label: "Quick win",       bg: "bg-brand-light/30",   text: "text-brand",        icon: "✓", accent: "border-l-brand" },
    milestone:       { label: "Milestone",       bg: "bg-brand-light/30",   text: "text-brand",        icon: "●", accent: "border-l-brand" },
    optimization:    { label: "Optimisation",    bg: "bg-info-light/30",    text: "text-info",         icon: "◐", accent: "border-l-info" },
    bankability:     { label: "Bankability",     bg: "bg-ai-light/30",      text: "text-ai",           icon: "✦", accent: "border-l-ai" },
  },
  criticality: {
    critical_path: { label: "Critical Path", bg: "bg-danger-light",     text: "text-danger",         icon: "⊘" },
    high:          { label: "High",          bg: "bg-warning-light",    text: "text-warning-text",   icon: "!" },
    medium:        { label: "Medium",        bg: "bg-info-light",       text: "text-info",           icon: "◐" },
    low:           { label: "Low",           bg: "bg-surface-tertiary", text: "text-text-secondary", icon: "○" },
  },
  toast: {
    // text-on-solid, never text-white: in dark mode every semantic fill
    // lightens, and white on it scores 2.3–2.9:1.
    success: { label: "Success", bg: "bg-brand",  text: "text-on-solid", icon: "✓" },
    error:   { label: "Error",   bg: "bg-danger", text: "text-on-solid", icon: "!" },
    info:    { label: "Info",    bg: "bg-info",   text: "text-on-solid", icon: "i" },
  },
};

// dot and accent are set: consumers interpolate them unguarded into
// classNames, and an absent field renders the literal string "undefined".
const UNKNOWN: Style = {
  label: "Unrecognised", bg: "bg-surface-tertiary", text: "text-text-secondary", icon: "?",
  dot: "bg-hairline", accent: "border-hairline",
};

/** Never throws. An unmapped status renders honestly rather than crashing. */
export const status = (kind: StatusKind, value: string | null | undefined): Style =>
  (value ? STATUS[kind]?.[value] : undefined) ?? UNKNOWN;

/** The full vocabulary of a kind — for filter chips and option lists. */
export const statusKeys = (kind: StatusKind): string[] => Object.keys(STATUS[kind]);

/* ===========================================================================
   PROVENANCE — where a value came from
   =========================================================================== */

export type Source = "manual" | "extracted" | "calculated" | "needs_review" | "empty";

/**
 * Purple means: the system did this for you.
 *
 * Extraction used to be blue, which put the product's single largest act of
 * automation — reading your documents — outside the colour that represents
 * automation. Blue now means status and nothing else. The consequence is that
 * a screen's purple density is a live readout of how much work Enereg did.
 */
const PROVENANCE: Record<Source, Style & { hint: string }> = {
  manual:       { label: "Manual",     icon: "✎", bg: "bg-surface-tertiary", text: "text-text-secondary", dot: "bg-hairline", hint: "Typed by a person on your team." },
  extracted:    { label: "Extracted",  icon: "✦", bg: "bg-ai-light",         text: "text-ai",             dot: "bg-ai",       hint: "Read from one of your uploaded documents." },
  calculated:   { label: "Calculated", icon: "✦", bg: "bg-ai-light",         text: "text-ai",             dot: "bg-ai",       hint: "Derived from other fields in your project." },
  needs_review: { label: "Review",     icon: "⚠", bg: "bg-warning-light",    text: "text-warning-text",   dot: "bg-warning-fill", hint: "We found this, but are not confident enough to use it." },
  empty:        { label: "Missing",    icon: "—", bg: "bg-danger-light",     text: "text-danger",         dot: "bg-danger",   hint: "Required, and not yet supplied." },
};

export const provenance = (s: string | null | undefined) =>
  PROVENANCE[(s ?? "empty") as Source] ?? PROVENANCE.empty;

/** True when the value was authored by the system rather than a person. */
export const isMachineAuthored = (s: string | null | undefined): boolean =>
  s === "extracted" || s === "calculated";

/* ===========================================================================
   CONFIDENCE
   =========================================================================== */

export type Tier = "auto" | "review" | "always-confirm";

/**
 * Extraction confidence thresholds (fractions, as the pipeline stores them).
 * AUTO_COMMIT is imported from the pipeline itself — the value that actually
 * gates auto-commit and the value the UI presents must be one constant.
 */
export const CONFIDENCE = {
  AUTO_COMMIT: CONFIDENCE_THRESHOLD,  // ≥80%: auto-committed (pipeline's value)
  NEEDS_REVIEW: 0.60,                 // 60–79%: needs human review
  LOW: 0.60,                          // <60%: low confidence warning
} as const;

/**
 * Fields where a wrong value is project-stopping. These never auto-commit,
 * even at 99% confidence.
 *
 *   F-006  tariff     — a wrong tariff invalidates the financial model
 *   P-002  capacity   — a wrong capacity routes to the wrong licence entirely
 *   S-011  title deed — land tenure is the one thing lenders re-verify
 */
export const ALWAYS_CONFIRM = new Set(ALWAYS_CONFIRM_FIELDS);

/** `confidence` is a percentage (0–100), matching what the UI displays. */
export function confidenceTier(confidence: number, fieldId?: string): Tier {
  if (fieldId && ALWAYS_CONFIRM.has(fieldId)) return "always-confirm";
  return band(confidence, "confidence") === "strong" ? "auto" : "review";
}

const TIER_STYLE: Record<Tier, Style> = {
  "auto":           { label: "Auto-committed", bg: "bg-brand-light",   text: "text-brand",         icon: "✓" },
  "review":         { label: "Review",         bg: "bg-warning-light", text: "text-warning-text",  icon: "⚠" },
  "always-confirm": { label: "Always confirm", bg: "bg-danger-light",  text: "text-danger",        icon: "!" },
};

export const tierStyle = (t: Tier): Style => TIER_STYLE[t];
